import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'classnames';
import s from './s.module.less';

interface IQuestionOrder {
    questionId: string;
    order: number;
    title: string;
    deleted?: boolean;
}

interface IQuestionOrderModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (questionOrder: IQuestionOrder[]) => void;
    questionOrder: IQuestionOrder[];
}

const SortableItem = ({
    id,
    title,
    order,
    onDelete,
    deleted = false,
}: {
    id: string;
    title: string;
    order: number;
    onDelete: (id: string) => void;
    deleted?: boolean;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id, disabled: deleted });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(deleted ? {} : { ...attributes, ...listeners })}
            className={cx(s.sortableItem, deleted && s.deletedItem)}
        >
            <div className={s.itemContent}>
                <span className={s.orderNumber}>
                    {deleted ? '×' : `${order}.`}
                </span>
                <span className={cx(s.itemTitle, deleted && s.deletedTitle)}>
                    {title}
                </span>
                {!deleted && (
                    <Popconfirm
                        title="Are you sure you want to delete this question?"
                        onConfirm={() => onDelete(id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            className={s.deleteButton}
                        />
                    </Popconfirm>
                )}
            </div>
        </div>
    );
};

const QuestionOrderModal: React.FC<IQuestionOrderModalProps> = ({
    visible,
    onCancel,
    onSave,
    questionOrder,
}) => {
    const [localOrder, setLocalOrder] = useState<IQuestionOrder[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    useEffect(() => {
        if (visible) {
            setLocalOrder([...questionOrder]);
        }
    }, [visible, questionOrder]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id && localOrder?.length > 0) {
            const activeIndex = localOrder.findIndex(
                (item) => item.questionId === active.id,
            );
            const overIndex = localOrder.findIndex(
                (item) => item.questionId === over?.id,
            );

            if (activeIndex === -1 || overIndex === -1) {
                return;
            }

            const newOrder = arrayMove(localOrder, activeIndex, overIndex);
            // 重新计算order值
            const updatedOrder = newOrder.map((item, index) => ({
                ...item,
                order: index + 1,
            }));
            setLocalOrder(updatedOrder);
        }
    };

    const handleDelete = (questionId: string) => {
        setLocalOrder((prev) =>
            prev.map((item) =>
                (item.questionId === questionId
                    ? { ...item, deleted: true }
                    : item),
            ),
        );
    };

    const handleRestore = (questionId: string) => {
        setLocalOrder((prev) =>
            prev.map((item) =>
                (item.questionId === questionId
                    ? { ...item, deleted: false }
                    : item),
            ),
        );
    };

    const handleSave = async () => {
        setSubmitting(true);
        try {
            // 只保存未删除的问题，并重新计算order
            const activeQuestions = localOrder
                .filter((item) => !item.deleted)
                .map((item, index) => ({
                    ...item,
                    order: index + 1,
                }));

            await onSave(activeQuestions);
        } catch (error) {
            message.error('Failed to save question order');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setLocalOrder([...questionOrder]);
        onCancel();
    };

    return (
        <Modal
            title="Set Question Order"
            open={visible}
            destroyOnClose
            onCancel={handleCancel}
            footer={
                <div>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        loading={submitting}
                        type="primary"
                        onClick={handleSave}
                    >
                        Save Order
                    </Button>
                </div>
            }
            width={600}
        >
            <div className={s.modalContent}>
                <p className={s.description}>
                    Drag and drop to reorder questions. This order will be used as the default sequence for the assessment.
                </p>

                <div className={s.sortContainer}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={localOrder
                                .filter((item) => !item.deleted)
                                .map((item) => item.questionId)
                            }
                            strategy={rectSortingStrategy}
                        >
                            {localOrder
                                .filter((item) => !item.deleted)
                                .map((item) => (
                                    <SortableItem
                                        key={item.questionId}
                                        id={item.questionId}
                                        title={item.title}
                                        order={item.order}
                                        onDelete={handleDelete}
                                    />
                                ))}
                        </SortableContext>
                    </DndContext>

                    {/* 显示已删除的问题 */}
                    {localOrder.some((item) => item.deleted) && (
                        <div className={s.deletedSection}>
                            <h4 className={s.deletedTitle}>Deleted Questions</h4>
                            {localOrder
                                .filter((item) => item.deleted)
                                .map((item) => (
                                    <div key={item.questionId} className={s.deletedItem}>
                                        <div className={s.itemContent}>
                                            <span className={s.orderNumber}>×</span>
                                            <span className={s.deletedTitle}>{item.title}</span>
                                            <Button
                                                type="text"
                                                size="small"
                                                onClick={() => handleRestore(item.questionId)}
                                                className={s.restoreButton}
                                            >
                                                Restore
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default QuestionOrderModal;
